import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { deriveRole, type UserRole } from "@/lib/roleUtils";

// Public route prefixes. Authenticated users can land on these (e.g. clicking
// their own share link) and must not be bounced to "/" by role enforcement.
// Keep in sync with PUBLIC_ROUTES in client/src/routes/index.tsx.
const PUBLIC_ROUTE_PREFIXES = [
  "/embed/forms",
  "/forgot-password",
  "/reset-password",
  "/quote",
  "/proposals",
  "/proposal/",
];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
}

// Allowed route prefixes for each role (route enforcement)
const ROLE_ALLOWED_ROUTES: Record<UserRole, string[]> = {
  superadmin: ["/superadmin", "/carriers", "/settings", "/internal/form-library"],
  broker: [
    "/agency", "/clients", "/prospects", "/plans", "/carriers", "/quoting",
    "/tasks", "/tickets", "/marketing", "/directory", "/reports",
    "/my-commissions", "/settings", "/emails", "/calendar", "/bug-reports",
    "/migration", "/renewals",
  ],
  employer: [
    "/employees", "/payroll", "/pto", "/time-off", "/contractors", "/benefits",
    "/plans", "/compliance", "/aca", "/hr", "/documents", "/reports",
    "/settings", "/emails", "/company", "/eligibility-milestones",
  ],
  employee: [
    "/onboarding", "/profile", "/commissions", "/employment", "/benefits",
    "/pto", "/bank-and-tax", "/documents", "/aca-compliance", "/support",
    "/settings", "/emails", "/company", "/reports",
  ],
  // GA (General Agency) portal — all pages live under /ga/*
  ga: ["/ga"],
};

function isRouteAllowed(pathname: string, allowedRoutes: string[]): boolean {
  return allowedRoutes.some(
    (route) => pathname === route || pathname.startsWith(route + "/")
  );
}

interface RoleContextValue {
  currentRole: UserRole;
  setCurrentRole: (role: UserRole) => void;
  user: any;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  // Initialize synchronously from URL/localStorage hint via deriveRole(null)
  const [currentRole, setCurrentRoleState] = useState<UserRole>(() => deriveRole(null));

  // Single setter that updates state + URL + localStorage together
  const setCurrentRole = useCallback((role: UserRole) => {
    setCurrentRoleState(role);
    try {
      localStorage.setItem("selectedRole", role);
      localStorage.setItem("userRole", role);
    } catch { /* ignore */ }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("role", role);
      window.history.replaceState(null, "", url.toString());
    } catch { /* ignore */ }
  }, []);

  // Once the user loads, reconcile the role with their actual permissions/associations
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const desired = deriveRole(user);
    if (desired !== currentRole) {
      setCurrentRole(desired);
    }
  }, [isAuthenticated, user, currentRole, setCurrentRole]);

  // Enforce route allow-list when role changes. Public routes (share links,
  // forgot-password, embed forms, etc.) are exempt — an authenticated user
  // following their own quote share link must reach PublicQuote, not the
  // role-default dashboard.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const pathname = window.location.pathname;
    if (isPublicPath(pathname)) return;
    const allowed = ROLE_ALLOWED_ROUTES[currentRole] || [];
    const ok = pathname === "/" || isRouteAllowed(pathname, allowed);
    if (!ok) {
      setLocation("/");
    }
  }, [currentRole, isAuthenticated, user, setLocation]);

  return (
    <RoleContext.Provider value={{ currentRole, setCurrentRole, user, isLoading, isAuthenticated }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    throw new Error("useRole must be used inside <RoleProvider>");
  }
  return ctx;
}
